'use client';

import {useCallback, useState} from 'react';
import clsx from 'clsx';

import {
	type ActionQuestionAnswer,
	submitComplaintGuide,
} from '@/modules/finkn/complaint-guide/actions';
import {Button} from '@/modules/foundation/components/button';
import {Container} from '@/modules/foundation/components/container';
import {ArrowRightIcon} from '@/modules/foundation/components/icons/arrow-right-icon';
import {ErrorCircleIcon} from '@/modules/foundation/components/icons/error-circle-icon';
import {Spinner} from '@/modules/foundation/components/spinner';

import type {ComplaintGuideQuestion} from '../complaint-guide';
import {useComplaintGuide} from '../guide-context';

type Props = {
	initialQuestions?: ComplaintGuideQuestion[];
	loginUrl?: string;
};

export const ComplaintGuideFooter = ({initialQuestions, loginUrl}: Props) => {
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const {
		goToNextStep,
		goToPreviousStep,
		setQuestions,
		currentStep,
		questions,
		currentAnswer,
		isLastQuestion,
		setShowResult,
		showResult,
		setAnswers,
		answers,
	} = useComplaintGuide();

	const handleStartGuide = useCallback(() => {
		if (initialQuestions) {
			setQuestions(initialQuestions);
		}
	}, [initialQuestions, setQuestions]);

	const handleShowResult = useCallback(() => {
		setShowResult(true);
	}, [setShowResult]);

	const handleDontShowResult = useCallback(() => {
		setShowResult(false);
	}, [setShowResult]);

	const handleEmptyQuestions = useCallback(() => {
		setQuestions([]);
		setAnswers([]);
	}, [setQuestions, setAnswers]);

	const handleSubmit = async () => {
		setLoading(true);
		setSubmitError(null);

		const answersToSubmit: ActionQuestionAnswer[] = answers
			.filter((answer) => answer.positiveAnswerId)
			.map((answer) => ({
				questionId: answer.questionId,
				// FIXME: fix react hooks eslint errors
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				answerId: answer.positiveAnswerId!,
			}));

		const result = await submitComplaintGuide(answersToSubmit);

		// Only reaches here if redirect didn't happen (error case)
		if (result && !result.success) {
			setSubmitError(
				result.error || 'Feil under sending av forespørsel!',
			);
		}
		setLoading(false);
	};

	return (
		<Container
			className={clsx(
				'border-t-2',
				'border-[#e2e8ee]',
				'bg-white',
				'py-4',
				'shadow-[0px_-3px_29px_-2px_rgba(0,67,121,0.07),0px_0px_6px_0px_rgba(0,67,121,0.00)]',
				'sticky',
				'bottom-0',
				'left-0',
				'right-0',
			)}
			hasBackground
		>
			<div
				className={clsx(
					'flex',
					'flex-col',
					showResult
						? 'min-[420px]:flex-row'
						: 'min-[345px]:flex-row',
					'justify-between',
					// showResult ? "" : 'items-center',
					'gap-4',
					// !showResult && 'h-[75px]',
					// !showResult && 'md:h-[85px]',
				)}
			>
				{/** We are on the first question, so if we go back we want to remove the guide
				 * questions to show the initial page */}
				{currentStep === 0 && questions.length > 0 && (
					<Button variant="secondary" onClick={handleEmptyQuestions}>
						<ArrowRightIcon aria-hidden className="rotate-180" />
						Tilbake
					</Button>
				)}

				{/* We are on a step that has a previous step so we can go back */}
				{currentStep > 0 && questions.length > 0 && !showResult && (
					<Button variant="secondary" onClick={goToPreviousStep}>
						<ArrowRightIcon aria-hidden className="rotate-180" />
						Tilbake
					</Button>
				)}

				{/* We are on the result page and want to go back to the last step */}
				{showResult && (
					<Button variant="secondary" onClick={handleDontShowResult}>
						<ArrowRightIcon aria-hidden className="rotate-180" />
						Tilbake
					</Button>
				)}

				{/* We have a positive answer, so we can go to the next step */}
				{currentAnswer?._type === 'positiveAnswer' &&
					!isLastQuestion && (
						<Button variant="success" onClick={goToNextStep}>
							Neste
							<ArrowRightIcon aria-hidden />
						</Button>
					)}

				{/* We are on the last question, so we want to show the result */}
				{currentAnswer?._type === 'positiveAnswer' &&
					isLastQuestion &&
					!showResult && (
						<Button variant="success" onClick={handleShowResult}>
							Neste
							<ArrowRightIcon aria-hidden />
						</Button>
					)}

				{/* Use the prefilled form if no overridden login url */}
				{showResult && !loginUrl?.length && (
					<div className="inline-flex flex-col">
						<Button variant="success" onClick={handleSubmit}>
							Gå til klageskjema
							{loading ? (
								<Spinner title="sender" />
							) : (
								<ArrowRightIcon aria-hidden />
							)}
						</Button>
						{submitError ? (
							<div
								className="mt-2 flex items-center justify-center gap-2 text-center text-red-700"
								aria-live="polite"
							>
								<ErrorCircleIcon aria-hidden />
								{submitError}
							</div>
						) : null}
					</div>
				)}

				{/* If wi have a login url, use this instead of the prefilled form */}
				{showResult && loginUrl?.length && (
					<div className="inline-flex flex-col">
						<Button
							variant="success"
							onClick={handleSubmit}
							asChild
						>
							<a href={loginUrl}>
								Gå til klageskjema
								{loading ? (
									<Spinner title="sender" />
								) : (
									<ArrowRightIcon aria-hidden />
								)}
							</a>
						</Button>
						{submitError ? (
							<div
								className="mt-2 flex items-center justify-center gap-2 text-center text-red-700"
								aria-live="polite"
							>
								<ErrorCircleIcon aria-hidden />
								{submitError}
							</div>
						) : null}
					</div>
				)}

				{/* We are on the initial page, so we want to set the questions to start */}
				{questions.length < 1 && (
					<Button
						variant="success"
						onClick={handleStartGuide}
						className="ml-auto"
					>
						Kom i gang
						<ArrowRightIcon aria-hidden />
					</Button>
				)}
			</div>
		</Container>
	);
};
